import { BrandMark } from "@/components/Logo";
import { Button } from "@/components/redvolt/kit";
import { Mail, LogIn, UserPlus, ShieldCheck } from "lucide-react";

export const InviteLanding = ({ onSignIn, onDismiss }) => (
  <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4">
    <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="invite-landing">
      <div className="mb-5 flex items-center gap-3">
        <BrandMark size={40} />
        <span className="text-lg font-bold tracking-tight text-slate-900">RedVolt</span>
      </div>
      <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-[#FFEBF0]">
        <Mail className="h-5 w-5 text-[#DC143C]" />
      </div>
      <h1 className="text-2xl font-bold text-slate-900">You've been invited</h1>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">
        You've been invited to join an organization on RedVolt. To accept, sign in or create
        your account using the <strong>same email address</strong> your invitation was sent to.
        We'll add you to the organization automatically once you're signed in.
      </p>
      <div className="mt-6 flex flex-col gap-2.5">
        <Button onClick={() => onSignIn("sign-up")} data-testid="invite-create-account">
          <UserPlus className="mr-1.5 h-4 w-4" /> Create your account
        </Button>
        <Button variant="outline" onClick={() => onSignIn("sign-in")} data-testid="invite-sign-in">
          <LogIn className="mr-1.5 h-4 w-4" /> I already have an account
        </Button>
        <p className="text-center text-xs text-slate-400">
          Already used this email with RedVolt before? Choose "I already have an account."
          If sign-up says the email is unavailable, sign in instead.
        </p>
      </div>
      <div className="mt-5 flex gap-2.5 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="invite-mfa-notice">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <div className="text-xs leading-relaxed text-amber-800">
          <p className="font-semibold">You'll set up two-factor authentication</p>
          <p className="mt-1">
            RedVolt requires an authenticator app (Google Authenticator, Microsoft Authenticator,
            Authy, or 1Password). During sign-up you'll be shown a <strong>QR code</strong>.
          </p>
          <p className="mt-1">
            <strong>On a phone?</strong> You can't scan the QR code with the same phone. Tap
            <strong> "Can't scan the code?"</strong> to reveal a long <strong>setup key</strong>, then:
          </p>
          <ol className="mt-1 list-decimal space-y-0.5 pl-4">
            <li>Copy the long setup key (the 20+ character code).</li>
            <li>Open your authenticator app → <strong>Add account → "Enter a setup key"</strong> → paste it.</li>
            <li>Your app now shows a rotating <strong>6-digit code</strong>.</li>
            <li>Come back here and type that <strong>6-digit code</strong> into the boxes.</li>
          </ol>
          <p className="mt-1">
            The long key goes <strong>into the app</strong> — only the app's 6-digit number goes in the boxes.
            Or start sign-up on a computer and scan the QR code with your phone.
          </p>
        </div>
      </div>
      <button onClick={onDismiss} className="mt-5 text-xs text-slate-400 hover:text-slate-600" data-testid="invite-dismiss">
        This isn't for me — go to the RedVolt home page
      </button>
    </div>
    <p className="mt-4 max-w-md text-center text-xs text-slate-400">
      Invitation links are secure, single-use, and expire after 14 days.
    </p>
  </div>
);

export default InviteLanding;
